import { type ChangeEvent, type DragEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CheckCircle2 } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import Title from '@/components/ui/Title';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { WaitingRoomFormValues } from '@/pages/create-webinar/WaitingRoomSection';
import type { IndexItem, WaitingRoomPage } from '@/features/webinar/types';
import PreviewWaitingRoomWebinar from './PreviewWaitingRoomWebinar';
import Loader from '@/components/ui/Loader';
import { cn } from '@/lib/utils';

const MAX_BACKGROUND_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_BACKGROUND_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

type QuizFormValues = {
  name: string;
  subtitle: string;
  questions: {
    id?: number;
    textContent: string;
    type: 'SINGLE_CHOICE';
    options: {
      textContent: string;
      correct: boolean;
      position?: number | null;
    }[];
  }[];
};

type SurveyFormValues = {
  title: string;
  subtitle: string;
  questions: {
    id: number;
    question: string;
    answerType: '1' | '2';
    answers: string[];
  }[];
};

export default function WaitingRoomBackground({
  visualIdentity,
  form,
  onBackgroundChange,
  onGradientChange,
  onImageUpload,
  backgroundImageUrl,
  selectedBackgroundImageFileName,
  mainImageUrl,
  mainImage,
  mainVideo,
  showMainMedia,
  indexItems,
  showPreWebinarActivity,
  hasWaitingRoomQuiz,
  hasWaitingRoomSurvey,
  interactionType,
  hasQuizFormData,
  hasSurveyFormData,
  quizForm,
  surveyForm,
  isEdit,
  isEvergreen,
  onSubmit,
  currentWaitingRoomPage,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  form: UseFormReturn<WaitingRoomFormValues>;
  onBackgroundChange: (value: WaitingRoomFormValues['background']) => void;
  onGradientChange: (value: string) => void;
  onImageUpload: (file: File) => void;
  backgroundImageUrl?: string | null;
  selectedBackgroundImageFileName?: string | null;
  mainImageUrl?: string | null;
  mainImage?: string | null;
  mainVideo?: string | null;
  showMainMedia?: boolean;
  indexItems?: IndexItem[];
  showPreWebinarActivity?: boolean;
  hasWaitingRoomQuiz?: boolean;
  hasWaitingRoomSurvey?: boolean;
  interactionType?: 'SURVEY' | 'QUIZ' | 'NONE';
  hasQuizFormData?: boolean;
  hasSurveyFormData?: boolean;
  quizForm?: UseFormReturn<QuizFormValues>;
  surveyForm?: UseFormReturn<SurveyFormValues>;
  isEdit?: boolean;
  isEvergreen?: boolean;
  onSubmit?: (data: WaitingRoomFormValues, backgroundModified?: boolean) => Promise<void>;
  currentWaitingRoomPage?: WaitingRoomPage | null;
}) {
  const { t } = useTranslation('webinars');
  const { t: tCW } = useTranslation('createWebinar');
  const background = form.watch('background');
  const selectedGradient = form.watch('backgroundGradient') ?? '1';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const isUploading = form.formState.isSubmitting;
  const [accordionValue, setAccordionValue] = useState<string[]>(
    isEdit ? [] : isEvergreen ? [] : ['item-1', 'item-2'],
  );

  useEffect(() => {
    if (isEdit) {
      setAccordionValue([]);
    } else {
      setAccordionValue(isEvergreen ? [] : ['item-1', 'item-2']);
    }
  }, [isEdit, isEvergreen]);

  const gradientOne = `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1 || '#000'}, ${visualIdentity?.gradient1Color2 || '#fff'})`;
  const gradientTwo = `linear-gradient(226deg, ${visualIdentity?.gradient2Color1 || '#000'} 0.2%, ${visualIdentity?.gradient2Color2 || '#fff'} 104.63%)`;
  const normalizedBackgroundImage =
    backgroundImageUrl && backgroundImageUrl.trim().length > 0
      ? decodeURIComponent(backgroundImageUrl)
      : undefined;
  const previewBackground =
    background === 'GRADIENT'
      ? selectedGradient === '2'
        ? gradientTwo
        : gradientOne
      : visualIdentity?.backgroundColor || '#FBFBFB';
  const previewBackgroundImage = background === 'IMAGE' ? normalizedBackgroundImage : undefined;

  const selectFile = useCallback(
    (file: File) => {
      if (!ACCEPTED_BACKGROUND_IMAGE_TYPES.includes(file.type)) {
        toast.error(t('waitingRoom.background.errors.unsupportedFormat'));
        return;
      }
      if (file.size > MAX_BACKGROUND_IMAGE_BYTES) {
        toast.error(t('waitingRoom.background.errors.fileTooLarge'));
        return;
      }
      onImageUpload(file);
    },
    [onImageUpload, t],
  );

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        selectFile(files[0]);
      }
    },
    [selectFile],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!isUploading) {
        setIsDragging(true);
      }
    },
    [isUploading],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      if (isUploading) {
        return;
      }

      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        selectFile(files[0]);
      }
    },
    [isUploading, selectFile],
  );
  return (
    <div>
      <Accordion
        type="multiple"
        value={accordionValue}
        onValueChange={setAccordionValue}
        className="space-y-8 [&>div]:after:hidden"
      >
        <AccordionItem value="item-1" className={cn(isEdit && 'accordion-page')}>
          <AccordionTrigger fillForm={currentWaitingRoomPage?.backgroundModified}>
            <Title level={2}>{t('waitingRoom.background.title')}</Title>
          </AccordionTrigger>

          <AccordionContent>
            <Controller
              name="background"
              control={form.control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    onBackgroundChange(value as WaitingRoomFormValues['background']);
                  }}
                  className="flex items-center gap-4"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="DEFAULT" id="DEFAULT" />
                    <Label htmlFor="DEFAULT" className="font-normal">
                      {t('waitingRoom.background.defaultColor')}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="GRADIENT" id="GRADIENT" />
                    <Label htmlFor="GRADIENT" className="font-normal">
                      {t('waitingRoom.background.gradient')}
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="IMAGE" id="IMAGE" />
                    <Label htmlFor="IMAGE" className="font-normal">
                      {t('waitingRoom.background.image')}
                    </Label>
                  </div>
                </RadioGroup>
              )}
            />
            {/* {form.formState.errors.background && (
              <p className="text-xs text-error mt-2">{form.formState.errors.background.message}</p>
            )} */}

            {background === 'GRADIENT' && visualIdentity && (
              <div className="relative pt-4">
                <Select value={selectedGradient} onValueChange={onGradientChange}>
                  <SelectTrigger label={t('waitingRoom.background.colorLabel')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-4 rounded border border-gray-200 shrink-0"
                          style={{
                            background: `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1 || '#000'}, ${visualIdentity?.gradient1Color2 || '#fff'})`,
                          }}
                        />
                        <span>{t('waitingRoom.background.gradient1')}</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="2">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-4 rounded border border-gray-200 shrink-0"
                          style={{
                            background: `linear-gradient(226deg, ${visualIdentity?.gradient2Color1 || '#000'} 0.2%, ${visualIdentity?.gradient2Color2 || '#fff'} 104.63%)`,
                          }}
                        />
                        <span>{t('waitingRoom.background.gradient2')}</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {background === 'IMAGE' && (
              <div className="pt-4">
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`rounded-lg p-4 border-dashed border-2 transition-colors ${
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-card hover:border-primary/50'
                  } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  {normalizedBackgroundImage ? (
                    <div className="flex items-center gap-4">
                      <img
                        src={normalizedBackgroundImage}
                        alt={t('waitingRoom.background.selectedFile')}
                        className="h-16 w-16 shrink-0 rounded-md border border-card object-cover"
                      />
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                        <span className="truncate text-sm">
                          {selectedBackgroundImageFileName ??
                            t('waitingRoom.background.selectedFile')}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        className="text-back border-black shrink-0"
                        disabled={isUploading}
                        onClick={handleClick}
                      >
                        {t('waitingRoom.background.replace')}
                      </Button>
                    </div>
                  ) : (
                    <div
                      className={`flex items-center justify-between gap-2 ${isUploading ? '' : 'cursor-pointer'}`}
                      onClick={handleClick}
                    >
                      <Label className="text-secondary font-normal">
                        {isUploading
                          ? t('waitingRoom.background.uploading')
                          : t('waitingRoom.background.dragAndDrop')}
                      </Label>
                      <Label className="text-secondary font-normal">
                        {t('waitingRoom.background.or')}
                      </Label>
                      <Button
                        variant="outline"
                        className="text-back border-black"
                        disabled={isUploading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClick();
                        }}
                      >
                        {t('waitingRoom.background.chooseFile')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <Button
              className="w-full mt-4"
              disabled={form.formState.isSubmitting}
              onClick={async () => {
                if (onSubmit) {
                  try {
                    await form.handleSubmit((data) => onSubmit(data, true))();
                    setAccordionValue((prev) => prev.filter((item) => item !== 'item-1'));
                  } catch (error) {
                    console.error('Erreur lors de la sauvegarde', error);
                  }
                }
              }}
            >
              {form.formState.isSubmitting && <Loader />} {tCW('save')}
            </Button>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="item-2" className={cn(isEdit && 'accordion-page')}>
          <AccordionTrigger>
            <Title level={2}>{t('waitingRoom.background.preview')}</Title>
          </AccordionTrigger>

          <AccordionContent className="w-full flex justify-center">
            <PreviewWaitingRoomWebinar
              visualIdentity={visualIdentity}
              theme={visualIdentity?.theme || 'LIGHT'}
              accentColor={visualIdentity?.accentColor || ''}
              backgroundColor={previewBackground}
              backgroundImage={previewBackgroundImage}
              mainImage={mainImage || mainImageUrl}
              mainVideo={mainVideo}
              showMainMedia={showMainMedia}
              contrastColor={visualIdentity?.contrastColor || ''}
              showLogo={form.watch('showLogo')}
              showSpeakers={form.watch('showSpeakers')}
              showGuests={form.watch('showGuests')}
              showIndex={form.watch('showIndex')}
              indexItems={indexItems}
              showPreWebinarActivity={showPreWebinarActivity}
              hasWaitingRoomQuiz={hasWaitingRoomQuiz}
              hasWaitingRoomSurvey={hasWaitingRoomSurvey}
              interactionType={interactionType}
              hasQuizFormData={hasQuizFormData}
              hasSurveyFormData={hasSurveyFormData}
              quizForm={quizForm}
              surveyForm={surveyForm}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
